import {
  Component,
  OnInit,
  AfterViewInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  ViewChildren,
  QueryList
} from '@angular/core';
import { AbstractControl, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import {
  FormEntryComponentClass,
  AppInjector,
  FormBroadcastConfig,
  FormBroadcast
} from '@ptl/forms';
import { Observable, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { Person } from './interfaces/person.interface';
import { IndividualMarketPersonsService } from './services/individual-market-persons.service';
import moment from 'moment';
import { ZipCountyStateStructure } from 'src/app/angular-elements/shared/interfaces/zipCountyStateStructure.interface';
import { DateService } from 'src/app/shared/services/date.service';
import type { Moment } from 'moment';
import { select } from '@ptl/angular-redux-store';
import { EffectiveDateStateStructure } from 'src/app/angular-elements/shared/components/configs/interfaces/effective-date.state-structure.interface';
import { MomentTimeZoneEnum } from 'src/app/shared/enums/moment-timezone.enum';
import { environment } from 'src/environments/environment';

/**
 * This component service 2 purposes:
 * 1) To exist as a general parent for: subscriber, dependent, and spousalPartner
 *    patterns (they share the same sort of functionality, layout)
 * 2) To server as a proxy for storing data in our payload states
 *    a) The child component will do their own form work, but they will want to
 *       save their data to the parent form via a stringified value in order to
 *       store their data using the angular-redux/form approach.
 *
 * This is mainly a way to link the angular-redux/forms, payloadStates, and formArray
 * (where formArrays are more/less busted within angular-redux/form)
 */

export interface PersonsControlAsJson {
  dob: string;
  gender: 'F' | 'M';
  tobaccoIndicator: boolean;
}
export interface PersonFormAsJson {
  persons: Person[];
}

@Component({
    selector: 'app-individual-market-persons',
    templateUrl: './individual-market-persons.component.html',
    styleUrls: ['./individual-market-persons.component.scss'],
    standalone: false
})
export class IndividualMarketPersonsComponent extends FormEntryComponentClass
  implements OnInit, AfterViewInit, OnDestroy {

  injector = AppInjector.getInjector();

  protected fb: UntypedFormBuilder;
  protected individualMarketPersonsService: IndividualMarketPersonsService;
  protected dateService: DateService;

  timeZone = MomentTimeZoneEnum[environment.configs.siteTimeZone];

  @ViewChild('templateCustom') templateCustom: ElementRef;
  @select(['appStates', 'EffectiveDate']) effectiveDate$: Observable<EffectiveDateStateStructure>;

  @select(['payloadStates', 'individualMarket', 'ratingInfo', 'spousal-partner']) spousalPartnerChanges$: Observable<any>;
  spousalPartnerChanged: Subscription;

  @select(['payloadStates', 'individualMarket', 'ratingInfo', 'dependents']) dependentsChanges$: Observable<any>;
  dependentsChanged: Subscription;

  applicantType: string; // "Your Details", "Spousal Partner Details", "Dependent # Details"
  allowRemoval = true; // Whether or not you can remove items from the form, i.e. you likely NEED a subscriber, so we disable it there

  personsForm: UntypedFormGroup;
  personsFormChanges$: Subscription;
  formChanges$: Subscription;

  hideCheckbox = false;

  formatEffDate: string; 
  effectiveDate: string;
  ageOfPerson: number;

  isOkToDisplayAgeInfo: boolean = false;
  isEffectiveDateValid: boolean = false;

  @ViewChildren('remove') removeLink: QueryList<ElementRef>;
  @ViewChild('liveRegion', { static: true })
  liveRegion!: ElementRef;

  alphaMap: Map<number, string> = (() => {
    const map = new Map();

    // Populate the map with all 26 letters of the alphabet
    for (let i = 0; i < 26; i++) {
      map.set(i, String.fromCharCode(65 + i));
    }

    return map;
  })();

  constructor() {
    super();

    this.fb = this.injector.get(UntypedFormBuilder);
    this.individualMarketPersonsService = this.injector.get(IndividualMarketPersonsService);
    this.dateService = this.injector.get(DateService);

    /** Template */
    this.form = this.fb.group({});

    /** Component Form */
    this.personsForm = this.fb.group({
      persons: this.fb.array([])
    });
  }


  ngOnInit(): void {

    this.form.addControl(this.entry.formControlAttributes.formControlName, null);

    this.isOkToDisplayAgeInfo = this.entry.custom.displayAgeInfo ? true : false;


    /** Get Data from state */
    const personsData = this.individualMarketPersonsService.getPersonsData(
      this.entry.formControlAttributes.connectPath,
      this.entry.formControlAttributes.formControlName
    );

    /** Pre-Populate */
    if (personsData) {
      let personIndex = 0;
      personsData.forEach(person => {
        (this.personsForm.get('persons') as UntypedFormArray).push(
          this.newPersonData(person, personIndex)
        );
        personIndex++
      });
    }

    this.formChanges$ = this.form.valueChanges.pipe(debounceTime(300)).subscribe((val: unknown) => {
      this.communicateSubFormToParentForm(this.personsForm.value as PersonFormAsJson);
      this.personsForm.markAllAsTouched();
      this.personsForm.markAsDirty();
    });

    this.personsFormChanges$ = this.personsForm.valueChanges
      .pipe(debounceTime(300))
      .subscribe((val: PersonFormAsJson) => {
        this.communicateSubFormToParentForm(val, true);
      });

    this.hideCheckbox = this.hideTobaccoQuestion();
    this.updateTobaccoIndicatorControlAvailability();

    this.effectiveDate$.subscribe(($event) => {
      (this.personsForm.controls.persons as UntypedFormArray).controls.forEach((a: UntypedFormGroup) => {
        Object.keys(a.controls).forEach((controlName: string) => {
          a.controls[controlName].updateValueAndValidity();
          this.getPersonsAge(a.controls['dob'].value, $event);
        });
      });
    });

    this.isEffectiveDateValid = this.effectiveDate !== "Invalid date"  && this.effectiveDate !== null && this.effectiveDate !== "";
  }

  trackByAttr(index: number, element: any): number {
    return element;
  }

  /**
   * Update the proxy form (to trigger state change) with raw data
   *
   * The intent is to keep *these* components functional and independent,
   * but to allow subsequent data access to align with the form config
   * @param val The value coming from the PersonsForm - the goal is to communicate from the sub-form to the dynamic form
   * @param patchValue Whether we need to update the parent form - only do this when invoking this function from a non parent-form observance
   */
  communicateSubFormToParentForm(val: PersonFormAsJson, patchValue?: boolean) {

    /** Person from form */
    const persons: Person[] = Object.assign([], val.persons);

    /** Persons from form to Persons for state */
    const personsAsObj: { [key: string]: Person } = (() => {

      const personObj: { [key: string]: Person } = {};

      /** Cast boolean to string so we can leverage the value later in form config */
      persons.forEach((person: Person, index: number) => {
        const statePerson: Person = {
          dob: person.dob,
          gender: person.gender,
          tobaccoIndicator: person.tobaccoIndicator,
          personId: index
         };
        Object.keys(person).filter(key => (person[key] !== null && person[key] !== undefined && !['dob', 'gender', 'tobaccoIndicator'].includes(key)))
          .forEach((key) => {
          statePerson[key] = person[key];
        });
        personObj[index] = statePerson;
      });

      return personObj;

    })();

    const dynamicObject: { [key: string]: { [key: string]: Person } } = {};
    dynamicObject[this.entry.formControlAttributes.formControlName] = personsAsObj;

    /** Update global form (trigger save to state) */
    if (patchValue) {
      this.form.patchValue(dynamicObject);
    }

    /** Invalidate the parent form if child form is invalid */
    if (!this.personsForm.valid) {
      this.form.get(this.entry.formControlAttributes.formControlName).setErrors({ 'persons-sub-form-invalid': true });
    } else {
      if (
        this.form.get(this.entry.formControlAttributes.formControlName) &&
        this.form.get(this.entry.formControlAttributes.formControlName).errors
      ) {
        delete this.form.get(this.entry.formControlAttributes.formControlName).errors['persons-sub-form-invalid'];
      }
    }

    this.updateTobaccoIndicatorControlAvailability();
  }

  announceMessage(message: string) {
    this.renderer.setProperty(this.liveRegion.nativeElement, 'textContent', message);

    setTimeout(() => {
      this.renderer.setProperty(this.liveRegion.nativeElement, 'textContent', '');
    }, 500);
  }

  newPersonData(
    person: Person = {} as Person,
    personIndex?: number
  ): UntypedFormGroup {

    const group = this.fb.group({
      dob: this.fb.control(person.dob || null, this.dobValidators().concat(Validators.required)),
      gender: this.fb.control(person.gender || null, [Validators.required]),
      tobaccoIndicator: this.fb.control(person.tobaccoIndicator || false),
      personId: personIndex ?? null
    });
    Object.keys(person).filter(key => (person[key] !== null && person[key] !== undefined && !['dob', 'gender', 'tobaccoIndicator'].includes(key)))
      .forEach(key => group?.addControl(key, this.fb.control(person[key], null)));
    return group;
  }

  ngOnDestroy() {
    if (this.personsFormChanges$) {
      this.personsFormChanges$.unsubscribe();
    }
    if (this.spousalPartnerChanged) {
      this.spousalPartnerChanged.unsubscribe();
    }
    if (this.dependentsChanged) {
      this.dependentsChanged.unsubscribe();
    }
  }

  removePerson(index: number) {
    (this.personsForm.get('persons') as UntypedFormArray).removeAt(index);
    this.announceMessage(this.applicantType + ' Details deleted');
  }

  get persons() {
    return (this.personsForm.get('persons') as UntypedFormGroup).controls;
  }

  maxTodayValidator(_this: IndividualMarketPersonsComponent) {
    return (control: AbstractControl): ValidationErrors | null => {
      let compareVal = control.value;

      const isAfterToday = moment(compareVal).isAfter(moment(), 'days');
      if (isAfterToday) {
        return { maxToday: { value: control.value } };
      }
      return null;
    };
  }

  minAgeValidator(_this: IndividualMarketPersonsComponent, ageInYears: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      let compareVal: Moment = null;

      if (_this?.parentForm?.["individualMarket.ratingInfo"]?.controls?.effectiveDate?.value) {

        /** Convert value from UTC to TZ from DOB, starting from isoString ending with moment */

        // No Timezone Date String
        const effectiveDateAsString = _this.parentForm["individualMarket.ratingInfo"].controls.effectiveDate.value;
        // Moment from ^
        compareVal = moment(effectiveDateAsString);
        // Timezone Date
        const date = new Date(compareVal.utc().year() + '/' + (compareVal.utc().month() + 1) + '/' + compareVal.utc().date());
        // Moment from ^
        compareVal = this.dateService.getDateAsMoment(date.toISOString());
      }

      if (compareVal) {
        const minValidDate = moment(compareVal).subtract(ageInYears, 'years').valueOf();
        const dateSelected = moment(control.value).valueOf();
        if (minValidDate < dateSelected) {
          return { minAgeInvalid: { value: control.value } };
        }
      }

      return null;
    };
  }

  maxAgeValidator(_this: IndividualMarketPersonsComponent, ageInYears: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      let compareVal: Moment = null;

      if (_this?.parentForm?.["individualMarket.ratingInfo"]?.controls?.effectiveDate?.value) {

        /** Convert value from UTC to TZ from DOB, starting from isoString ending with moment */

        // No Timezone Date String
        const effectiveDateAsString = _this.parentForm["individualMarket.ratingInfo"].controls.effectiveDate.value;
        // Moment from ^
        compareVal = moment(effectiveDateAsString);
        // Timezone Date
        const date = new Date(compareVal.utc().year() + '/' + (compareVal.utc().month() + 1) + '/' + compareVal.utc().date());
        // Moment from ^
        compareVal = this.dateService.getDateAsMoment(date.toISOString());
      }

      if (compareVal) {
        const maxValidDate = moment(compareVal).subtract(ageInYears, 'years').valueOf();
        const dateSelected = moment(control.value).valueOf();

        if (maxValidDate > dateSelected) {
          return { maxAgeInvalid: { value: control.value } };
        }
      }

      return null;

    };
  }

  over65Validator(_this: IndividualMarketPersonsComponent, ageInYears: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      let compareVal: Moment = null;

      if (_this?.parentForm?.["individualMarket.ratingInfo"]?.controls?.effectiveDate?.value) {

        /** Convert value from UTC to TZ from DOB, starting from isoString ending with moment */

        // No Timezone Date String
        const effectiveDateAsString = _this.parentForm["individualMarket.ratingInfo"].controls.effectiveDate.value;
        // Moment from ^
        compareVal = moment(effectiveDateAsString);
        // Timezone Date
        const date = new Date(compareVal.utc().year() + '/' + (compareVal.utc().month() + 1) + '/' + compareVal.utc().date());
        // Moment from ^
        compareVal = this.dateService.getDateAsMoment(date.toISOString());
      }

      if (compareVal) {
        const maxValidDate = moment(compareVal).subtract(ageInYears, 'years').valueOf();
        const dateSelected = moment(control.value).valueOf();

        if (maxValidDate >= dateSelected) {
          const event: FormBroadcast = {
            formControlName: 'over65',
            type: 'valueChange',
            value: 'over65'
          };
          _this.formStreamTrigger.triggerFormStream(event);
        } else {
          const event: FormBroadcast = {
            formControlName: 'over65',
            type: 'valueChange',
            value: 'under65'
          };
          _this.formStreamTrigger.triggerFormStream(event);
        }
      }

      return null;

    };
  }

  /** Over-Ridden in Children Components */
  dobValidators() {
    const validators = [];
    const customControlSettings: {
      minAge: number;
      maxAge: number;
      medicareEligibleAge?: number;
    } = this.entry.custom;

    if (customControlSettings) {

      validators.push(this.maxTodayValidator(this));

      /** Minimum Age */
      if (customControlSettings.minAge != null) {
        validators.push(this.minAgeValidator(this, customControlSettings.minAge));
      }

      /** Maximum Age */
      if (customControlSettings.maxAge != null) {
        validators.push(this.maxAgeValidator(this, customControlSettings.maxAge));
      }

      /** Maximum Age */
      if (customControlSettings?.medicareEligibleAge > 50) {
        validators.push(this.over65Validator(this, customControlSettings.medicareEligibleAge));
      }

    }

    return validators;
  }

  broadcastToFormStream(broadcastConfig: FormBroadcastConfig[]) {

    if (broadcastConfig && broadcastConfig.length > 0) {
      broadcastConfig.forEach((br) => {
        broadcastConfig.forEach((broadcast) => {
          switch (broadcast.type) {
            case 'valueChange':
              this.formControls[this.entry.formControlAttributes.formControlName].valueChanges.pipe(debounceTime(5)).subscribe(newValue => {

                const broadCastValue = broadcast.signal === 'value' ? newValue : broadcast.signal;

                const event: FormBroadcast = {
                  formControlName: this.entry.formControlAttributes.formControlName,
                  type: 'valueChange',
                  value: broadCastValue
                };

                this.formStreamTrigger.triggerFormStream(event);
              });
              break;
            default:
              break;
          }
        });
      });
    }

  }

  hideTobaccoQuestion() {
    const zipCountyState: ZipCountyStateStructure = this.reducerUtilityService.getComponentStates().ZipCounty;
    // TODO: this should be configurable through form as json...
    if (this.entry.custom?.hideTobaccoQuestion || ['NY', 'MN'].includes(zipCountyState?.county?.state)) {
      return true;
    } else {
      return false;
    }
  }

  updateTobaccoIndicatorControlAvailability(): void {
    if (this.entry.custom.minSmokingAge) {

      /** If dependent is not of legal smoking age, clear and disable tobacco checkbox */
      const minValidSmokingAge = moment().subtract(this.entry.custom.minSmokingAge, 'years').valueOf();

      this.personsForm.get('persons').value.forEach((person: PersonsControlAsJson, i: number) => {

        const currentPerson = (this.personsForm.get('persons') as UntypedFormArray).at(i);
        const dateSelected = moment(person.dob).valueOf();

        if (minValidSmokingAge < dateSelected) {
          /** Emitting the event loops eternal */
          currentPerson.get('tobaccoIndicator').disable({ emitEvent: false });
          currentPerson.get('tobaccoIndicator').setValue(false, { emitEvent: false });
        } else {
          /** Emitting the event loops eternal */
          currentPerson.get('tobaccoIndicator').enable({ emitEvent: false });
        }

      });

    } else {
      // console.warn('Please provide a custom.minSmokingAge value');
    }
  }

  getPersonsAge(dobValue, $event?){;
    if($event){
      this.isEffectiveDateValid = this.effectiveDate !== "Invalid date"  && this.effectiveDate !== null && this.effectiveDate !== "";
      this.formatEffDate = moment($event.effectiveDate).tz(this.timeZone).format('MMMM D, YYYY');
      this.effectiveDate = $event.effectiveDate;
    }else{
      const storedEffectiveDate = this.reducerUtilityService.getPayloadStates().individualMarket.ratingInfo.effectiveDate
      this.isEffectiveDateValid = this.reducerUtilityService.getPayloadStates().individualMarket.ratingInfo.effectiveDate && this.reducerUtilityService.getPayloadStates().individualMarket.ratingInfo.effectiveDate !== "";
      this.formatEffDate = moment(storedEffectiveDate).tz(this.timeZone).format('MMMM D, YYYY');
      this.effectiveDate = storedEffectiveDate;
    }
    

    if(dobValue && this.isOkToDisplayAgeInfo && this.isEffectiveDateValid){
      const effDateAsMoment = moment(this.effectiveDate);
      const dobAsMoment = moment(dobValue)
      return effDateAsMoment.diff(dobAsMoment, 'years');
    }
  };

  dobAsOfTodayByIndex(personIndex: number): string {
    const personsAge = this.getPersonsAge(this.persons[personIndex].get('dob').value);
    return `${personsAge} years old as of ${this.formatEffDate}`;
  }
}
